import { useMemo } from "react";
import moment from "moment";
import { useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import {
  useGetStoredSelectedBranches,
  useGetStoredSelectedClients,
  useGetStoredSelectedDrivers,
  useGetStoredSelectedSegments,
  useGetStoredSelectedSuppliers,
  useGetStoredSelectedVehicles,
  useGetStoredIncludeNullBranch,
  useGetStoredIncludeNullSegment,
  useGetStoredIncludeNullClient,
  useGetStoredIncludeNullSupplier,
  useGetStoredIncludeNullVehicle,
  useGetStoredIncludeNullDriver,
  useIsTreeReady,
} from "@store/tmsAssetTree/tmsAssetTreeSlice";
import type { DashboardFilter } from "../../types/dashboard";

export interface TransactionFilters {
  startDate: string;
  endDate: string;
  isTreeReady: boolean;
  selectedVehicles: string[];
  requestFilters: DashboardFilter;
  listFilters: DashboardFilter;
  previousMonthFilters: DashboardFilter;
}

export const useTransactionFilters = (): TransactionFilters => {
  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();
  const selectedBranches = useGetStoredSelectedBranches();
  const selectedSegments = useGetStoredSelectedSegments();
  const selectedClients = useGetStoredSelectedClients();
  const selectedSuppliers = useGetStoredSelectedSuppliers();
  const selectedVehicles = useGetStoredSelectedVehicles();
  const selectedDrivers = useGetStoredSelectedDrivers();
  const includeNullBranch = useGetStoredIncludeNullBranch();
  const includeNullSegment = useGetStoredIncludeNullSegment();
  const includeNullClient = useGetStoredIncludeNullClient();
  const includeNullSupplier = useGetStoredIncludeNullSupplier();
  const includeNullVehicle = useGetStoredIncludeNullVehicle();
  const includeNullDriver = useGetStoredIncludeNullDriver();
  const isTreeReady = useIsTreeReady();

  const requestFilters = useMemo<DashboardFilter>(() => {
    const payload: DashboardFilter = { startDate, endDate };
    if (selectedBranches?.length) payload.branchIds = selectedBranches;
    if (selectedSegments?.length) payload.segmentIds = selectedSegments;
    if (selectedClients?.length) payload.clientIds = selectedClients;
    if (selectedSuppliers?.length) payload.supplierIds = selectedSuppliers;
    if (selectedVehicles?.length) payload.vehicleIds = selectedVehicles;
    if (selectedDrivers?.length) payload.driverIds = selectedDrivers;
    payload.includeNullBranch = includeNullBranch;
    payload.includeNullSegment = includeNullSegment;
    payload.includeNullClient = includeNullClient;
    payload.includeNullSupplier = includeNullSupplier;
    payload.includeNullVehicle = includeNullVehicle;
    payload.includeNullDriver = includeNullDriver;
    return payload;
  }, [
    endDate,
    selectedBranches,
    selectedClients,
    selectedDrivers,
    selectedSegments,
    selectedSuppliers,
    selectedVehicles,
    includeNullBranch,
    includeNullSegment,
    includeNullClient,
    includeNullSupplier,
    includeNullVehicle,
    includeNullDriver,
    startDate,
  ]);

  const listFilters = useMemo<DashboardFilter>(
    () => ({
      branchIds: selectedBranches?.length ? selectedBranches : undefined,
      segmentIds: selectedSegments?.length ? selectedSegments : undefined,
      clientIds: selectedClients?.length ? selectedClients : undefined,
      supplierIds: selectedSuppliers?.length ? selectedSuppliers : undefined,
      vehicleIds: selectedVehicles?.length ? selectedVehicles : undefined,
      driverIds: selectedDrivers?.length ? selectedDrivers : undefined,
      includeNullBranch,
      includeNullSegment,
      includeNullClient,
      includeNullSupplier,
      includeNullVehicle,
      includeNullDriver,
    }),
    [
      selectedBranches,
      selectedClients,
      selectedDrivers,
      selectedSegments,
      selectedSuppliers,
      selectedVehicles,
      includeNullBranch,
      includeNullSegment,
      includeNullClient,
      includeNullSupplier,
      includeNullVehicle,
      includeNullDriver,
    ]
  );

  const previousMonthFilters = useMemo<DashboardFilter>(() => {
    const referenceStart = moment(startDate, "YYYY-MM-DD");
    const previousStart = referenceStart.clone().subtract(1, "month").startOf("month");
    const previousEnd = previousStart.clone().endOf("month");

    return {
      ...requestFilters,
      startDate: previousStart.format("YYYY-MM-DD"),
      endDate: previousEnd.format("YYYY-MM-DD"),
    };
  }, [requestFilters, startDate]);

  return {
    startDate,
    endDate,
    isTreeReady,
    selectedVehicles,
    requestFilters,
    listFilters,
    previousMonthFilters,
  };
};
